import { Pipe, PipeTransform } from '@angular/core';
import { TabloFaceToPerson } from '@ui/tablo-generator/types';

export type GroupMemberPhotoFitMode = 'pointCover' | 'coverTop';

export interface GroupMemberLegacyCrop {
  x: number;
  y: number;
  scale: number;
}

export interface GroupMemberPhotoSource {
  fromHand?: boolean;
  handCrop?: GroupMemberLegacyCrop | null;
}

export interface GroupMemberPhotoRenderConfig {
  containerTransform: string;
  point: {
    x: number;
    y: number;
  };
  scale: number;
  requestedImageScale: number;
  fitMode: GroupMemberPhotoFitMode;
  rotation: number;
}

const DEFAULT_MEMBER_PHOTO_POINT = Object.freeze({
  x: 50,
  y: 50,
});

const DEFAULT_MEMBER_PHOTO_SCALE = 1;
const DEFAULT_MEMBER_PHOTO_TRANSFORM = 'scale(1) translate(0px, 0px)';

@Pipe({
    name: 'groupMemberPhotoRenderConfig',
    standalone: false
})
export class GroupMemberPhotoRenderConfigPipe implements PipeTransform {
  transform(
    photo: GroupMemberPhotoSource | null | undefined,
    autoCrop: GroupMemberLegacyCrop | null | undefined,
    memberDetails:
      | Pick<TabloFaceToPerson, 'x' | 'y' | 'scale' | 'rotation'>
      | null
      | undefined,
    maximizePhoto: boolean | null | undefined,
  ): GroupMemberPhotoRenderConfig {
    const scale = maximizePhoto
      ? DEFAULT_MEMBER_PHOTO_SCALE
      : memberDetails?.scale ?? DEFAULT_MEMBER_PHOTO_SCALE;

    return {
      containerTransform: maximizePhoto
        ? this.getMaximizedContainerTransform(photo, autoCrop, memberDetails)
        : this.getContainerTransform(photo, autoCrop),
      point: maximizePhoto
        ? this.getMaximizedPoint(memberDetails)
        : this.getPoint(memberDetails),
      scale,
      requestedImageScale: scale,
      fitMode: maximizePhoto ? 'coverTop' : 'pointCover',
      rotation: maximizePhoto ? 0 : memberDetails?.rotation ?? 0,
    };
  }

  private getPoint(
    memberDetails: Pick<TabloFaceToPerson, 'x' | 'y'> | null | undefined,
  ) {
    return {
      x: memberDetails?.x ?? DEFAULT_MEMBER_PHOTO_POINT.x,
      y: memberDetails?.y ?? DEFAULT_MEMBER_PHOTO_POINT.y,
    };
  }

  private getMaximizedPoint(
    memberDetails: Pick<TabloFaceToPerson, 'x'> | null | undefined,
  ) {
    return {
      x: memberDetails?.x ?? DEFAULT_MEMBER_PHOTO_POINT.x,
      y: DEFAULT_MEMBER_PHOTO_POINT.y,
    };
  }

  private getMaximizedContainerTransform(
    photo: GroupMemberPhotoSource | null | undefined,
    autoCrop: GroupMemberLegacyCrop | null | undefined,
    memberDetails: Pick<TabloFaceToPerson, 'x'> | null | undefined,
  ) {
    if (memberDetails) {
      return DEFAULT_MEMBER_PHOTO_TRANSFORM;
    }

    if (photo?.fromHand && photo.handCrop) {
      return `scale(${DEFAULT_MEMBER_PHOTO_SCALE}) translate(${-(
        photo.handCrop.x ?? 0
      )}%, 0%)`;
    }

    if (autoCrop) {
      return `scale(${DEFAULT_MEMBER_PHOTO_SCALE}) translate(${
        autoCrop.x ?? 0
      }px, 0px)`;
    }

    return DEFAULT_MEMBER_PHOTO_TRANSFORM;
  }

  private getContainerTransform(
    photo: GroupMemberPhotoSource | null | undefined,
    autoCrop: GroupMemberLegacyCrop | null | undefined,
  ): string {
    if (photo?.fromHand && photo.handCrop) {
      return `scale(${photo.handCrop.scale ?? 1}) translate(${-(
        photo.handCrop.x ?? 0
      )}%, ${-(photo.handCrop.y ?? 0)}%)`;
    }

    if (autoCrop) {
      return `scale(${autoCrop.scale ?? 1}) translate(${autoCrop.x ?? 0}px, ${
        autoCrop.y ?? 0
      }px)`;
    }

    return DEFAULT_MEMBER_PHOTO_TRANSFORM;
  }
}
